import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  HiUser,
  HiMail,
  HiPhone,
  HiLocationMarker,
  HiOfficeBuilding,
  HiDocumentText,
  HiCheckCircle,
  HiInformationCircle,
  HiSparkles
} from 'react-icons/hi';
import { API_URL } from '../../config/apiConfig';

const VendorRegister = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    businessName: '',
    email: '',
    phone: '',
    state: '',
    city: '',
    completeAddress: {
      street: '',
      landmark: '',
      pinCode: ''
    },
    businessRegistrationType: 'other',
    businessRegistrationCertificate: ''
  });
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        completeAddress: {
          ...prev.completeAddress,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.fullName || !formData.email || !formData.phone ||
      !formData.state || !formData.city || !formData.completeAddress.street ||
      !formData.completeAddress.pinCode) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate phone number
    if (!/^[0-9]{10}$/.test(formData.phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    // Validate PIN code
    if (!/^[0-9]{6}$/.test(formData.completeAddress.pinCode)) {
      toast.error('Please enter a valid 6-digit PIN code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/vendor/register`, formData);

      if (response.data.status) {
        toast.success('Registration successful! You will be contacted once your account is verified.');
        navigate('/vendor/login');
      } else {
        toast.error(response.data.message || 'Registration failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Puducherry'
  ];

  const nextStep = () => {
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const isStepValid = (step) => {
    switch (step) {
      case 1:
        return formData.fullName && formData.email && formData.phone;
      case 2:
        return formData.state && formData.city && formData.completeAddress.street && formData.completeAddress.pinCode;
      case 3:
        return true; // Business info is optional
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-white/30 backdrop-blur-3xl">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 via-indigo-100/20 to-purple-100/20"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="mx-auto h-20 w-20 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shadow-xl mb-6">
            <HiSparkles className="h-10 w-10 text-white" />
          </div>
          <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-indigo-900 to-purple-900 bg-clip-text text-transparent">
            Join CabZigo
          </h2>
          <p className="mt-3 text-lg font-medium text-indigo-600">Partner with us and grow your business</p>
          <div className="mt-4 h-1 w-24 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full mx-auto"></div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-8">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${currentStep >= step
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg'
                    : 'bg-gray-200 text-gray-600'
                  }`}>
                  {currentStep > step ? <HiCheckCircle className="w-6 h-6" /> : step}
                </div>
                {step < 3 && (
                  <div className={`w-16 h-1 mx-4 ${currentStep > step ? 'bg-gradient-to-r from-indigo-500 to-purple-500' : 'bg-gray-200'
                    }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-600 mt-2 px-8">
            <span>Personal Info</span>
            <span>Address Details</span>
            <span>Business Info</span>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-lg shadow-2xl rounded-3xl border border-white/20 overflow-hidden">
          <div className="px-8 py-10 sm:p-12">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Step 1: Basic Information */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Personal Information</h3>
                    <p className="text-gray-600">Tell us about yourself and your business</p>
                  </div>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
                        Full Name / Business Owner Name *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <HiUser className="h-5 w-5 text-indigo-400" />
                        </div>
                        <input
                          type="text"
                          name="fullName"
                          id="fullName"
                          required
                          value={formData.fullName}
                          onChange={handleInputChange}
                          className="block w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gray-50/50"
                          placeholder="Enter your full name"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="businessName" className="block text-sm font-semibold text-gray-700 mb-2">
                        Business Name (Optional)
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <HiOfficeBuilding className="h-5 w-5 text-indigo-400" />
                        </div>
                        <input
                          type="text"
                          name="businessName"
                          id="businessName"
                          value={formData.businessName}
                          onChange={handleInputChange}
                          className="block w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gray-50/50"
                          placeholder="Enter business name"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <HiMail className="h-5 w-5 text-indigo-400" />
                        </div>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="block w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gray-50/50"
                          placeholder="Enter email address"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <HiPhone className="h-5 w-5 text-indigo-400" />
                        </div>
                        <input
                          type="tel"
                          name="phone"
                          id="phone"
                          required
                          maxLength="10"
                          pattern="[0-9]{10}"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="block w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gray-50/50"
                          placeholder="10-digit mobile number"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Address Information */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Address Information</h3>
                    <p className="text-gray-600">Where is your business located?</p>
                  </div>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="state" className="block text-sm font-semibold text-gray-700 mb-2">
                        State *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <HiLocationMarker className="h-5 w-5 text-indigo-400" />
                        </div>
                        <select
                          name="state"
                          id="state"
                          required
                          value={formData.state}
                          onChange={handleInputChange}
                          className="block w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gray-50/50"
                        >
                          <option value="">Select State</option>
                          {indianStates.map(state => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="city" className="block text-sm font-semibold text-gray-700 mb-2">
                        City *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <HiLocationMarker className="h-5 w-5 text-indigo-400" />
                        </div>
                        <input
                          type="text"
                          name="city"
                          id="city"
                          required
                          value={formData.city}
                          onChange={handleInputChange}
                          className="block w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gray-50/50"
                          placeholder="Enter city"
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="address.street" className="block text-sm font-semibold text-gray-700 mb-2">
                        Street Address *
                      </label>
                      <input
                        type="text"
                        name="address.street"
                        id="address.street"
                        required
                        value={formData.completeAddress.street}
                        onChange={handleInputChange}
                        className="block w-full px-4 py-4 border-2 border-gray-200 rounded-2xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gray-50/50"
                        placeholder="House/Shop number, street name"
                      />
                    </div>

                    <div>
                      <label htmlFor="address.landmark" className="block text-sm font-semibold text-gray-700 mb-2">
                        Landmark (Optional)
                      </label>
                      <input
                        type="text"
                        name="address.landmark"
                        id="address.landmark"
                        value={formData.completeAddress.landmark}
                        onChange={handleInputChange}
                        className="block w-full px-4 py-4 border-2 border-gray-200 rounded-2xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gray-50/50"
                        placeholder="Near landmark"
                      />
                    </div>

                    <div>
                      <label htmlFor="address.pinCode" className="block text-sm font-semibold text-gray-700 mb-2">
                        PIN Code *
                      </label>
                      <input
                        type="text"
                        name="address.pinCode"
                        id="address.pinCode"
                        required
                        maxLength="6"
                        pattern="[0-9]{6}"
                        value={formData.completeAddress.pinCode}
                        onChange={handleInputChange}
                        className="block w-full px-4 py-4 border-2 border-gray-200 rounded-2xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gray-50/50"
                        placeholder="6-digit PIN code"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Business Registration */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Business Registration</h3>
                    <p className="text-gray-600">Optional business information (helps with verification)</p>
                  </div>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="businessRegistrationType" className="block text-sm font-semibold text-gray-700 mb-2">
                        Registration Type
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <HiDocumentText className="h-5 w-5 text-indigo-400" />
                        </div>
                        <select
                          name="businessRegistrationType"
                          id="businessRegistrationType"
                          value={formData.businessRegistrationType}
                          onChange={handleInputChange}
                          className="block w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gray-50/50"
                        >
                          <option value="other">Not Registered</option>
                          <option value="shop_establishment">Shop & Establishment</option>
                          <option value="msme">MSME</option>
                          <option value="gst">GST Registration</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="businessRegistrationCertificate" className="block text-sm font-semibold text-gray-700 mb-2">
                        Registration Certificate URL
                      </label>
                      <input
                        type="url"
                        name="businessRegistrationCertificate"
                        id="businessRegistrationCertificate"
                        value={formData.businessRegistrationCertificate}
                        onChange={handleInputChange}
                        className="block w-full px-4 py-4 border-2 border-gray-200 rounded-2xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gray-50/50"
                        placeholder="Upload link or leave empty"
                      />
                    </div>
                  </div>

                  {/* Information Box */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <HiInformationCircle className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-blue-900 mb-3">
                          What happens next?
                        </h3>
                        <div className="text-sm text-blue-800 space-y-2">
                          <div className="flex items-center">
                            <HiCheckCircle className="h-4 w-4 text-green-600 mr-2" />
                            <span>Application review within 24-48 hours</span>
                          </div>
                          <div className="flex items-center">
                            <HiCheckCircle className="h-4 w-4 text-green-600 mr-2" />
                            <span>Email notification with login credentials</span>
                          </div>
                          <div className="flex items-center">
                            <HiCheckCircle className="h-4 w-4 text-green-600 mr-2" />
                            <span>Start adding drivers and managing your fleet</span>
                          </div>
                          <div className="flex items-center">
                            <HiCheckCircle className="h-4 w-4 text-green-600 mr-2" />
                            <span>Begin earning with CabZigo partnership</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between pt-8 border-t border-gray-200">
                <div className="flex space-x-4">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={prevStep}
                      className="inline-flex items-center px-6 py-3 border-2 border-gray-300 rounded-2xl text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                    >
                      ← Previous
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => navigate('/vendor/login')}
                    className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors duration-200"
                  >
                    Already have an account? Login
                  </button>
                </div>

                <div className="flex space-x-4">
                  {currentStep < 3 ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      disabled={!isStepValid(currentStep)}
                      className="inline-flex items-center px-8 py-3 border border-transparent rounded-2xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-105"
                    >
                      Continue →
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center px-8 py-3 border border-transparent rounded-2xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-105"
                    >
                      {loading ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                          Submitting...
                        </div>
                      ) : (
                        <>
                          <HiCheckCircle className="h-5 w-5 mr-2" />
                          Complete Registration
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorRegister;
